// 004 (FR-027–FR-032, FR-038, FR-048; BR-1): the delivery pin picker. A fixed centre pin over the
// map; the pin counts as placed only after the parent moves the map (drag, tap, keyboard pan), uses
// current location (US5) or picks a search result (FR-029). Search shows only with a geocoder.
// The map fails after 10 s without ready or 8 tile errors (FR-038). Coordinates are never logged.
import { useEffect, useReducer, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { useMapServices } from "./MapProvider";
import { initialPinState, pinReducer, READY_TIMEOUT_MS } from "./pinState";
import type { LatLng, Place, PlaceAddress } from "./types";

export const PIN_COPY = {
  title: "Delivery pin",
  hint: "Drop the pin where the order should be delivered",
  chip: "Drag map to move the pin",
  placed: "Pin dropped",
  placedAt: (d: PlaceAddress) =>
    `Pin dropped at ${[d.area, [d.city, d.pincode].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ")}`,
  filled: "Address fields below are filled from the pin. Edit them if needed.",
  mapLabel:
    "Map for the delivery pin. Drag the map, or use the arrow keys, to move the pin under the centre marker.",
  failed: "We couldn't load the map. Check your connection and try again.",
  tryAgain: "Try again",
  search: "Search for a place",
  goToCurrentLocation: "Use current location",
  locationFailed:
    "We couldn't get your location. Move the map to place the pin.",
} as const;

export type PinValue = { placed: boolean; center: LatLng | null };

const MAP_HEIGHT = 240;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { gap: t.space.sm },
    mapBox: {
      height: MAP_HEIGHT,
      borderRadius: t.radius.panel,
      overflow: "hidden",
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      backgroundColor: t.color.surfaceAlt,
    },
    overlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    },
    pinHead: {
      width: t.space.xl,
      height: t.space.xl,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.primary,
      borderWidth: t.borderWidth.focusRing,
      borderColor: t.color.surface,
    },
    pinStem: {
      width: t.borderWidth.focusRing,
      height: t.space.md,
      backgroundColor: t.color.primary,
    },
    pin: { alignItems: "center", marginBottom: t.space.x3l },
    chip: {
      position: "absolute",
      top: t.space.sm,
      alignSelf: "center",
      paddingHorizontal: t.space.md,
      paddingVertical: t.space.xxs,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.surface,
      pointerEvents: "none",
    },
    failed: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      gap: t.space.md,
      padding: t.space.lg,
    },
    error: { color: t.color.danger },
    result: { alignItems: "flex-start", paddingHorizontal: t.space.md },
  }),
);

export function PinPicker({
  savedPin,
  onChange,
  onDescribed,
  error,
  locate,
}: {
  savedPin?: LatLng;
  onChange: (value: PinValue) => void;
  onDescribed?: (d: PlaceAddress | null) => void;
  error?: string | null;
  /** US5: "Use current location"; hidden until provided. Resolves a position or null on failure. */
  locate?: () => Promise<LatLng | null>;
}) {
  const styles = useStyles();
  const { PinMap, geocoder, config } = useMapServices();
  const [state, dispatch] = useReducer(pinReducer, savedPin, initialPinState);
  const [recenter, setRecenter] = useState<
    { center: LatLng; seq: number } | undefined
  >();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Place[]>([]);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [initialCenter] = useState(() => ({
    ...(savedPin ?? config.defaultCenter),
    zoom: savedPin ? 17 : config.defaultCenter.zoom,
  }));

  // Report the pin up (only when it changes).
  const lastSent = useRef("");
  useEffect(() => {
    const value: PinValue = {
      placed: state.placed,
      center: state.placed ? state.center : null,
    };
    const key = JSON.stringify(value);
    if (key !== lastSent.current) {
      lastSent.current = key;
      onChange(value);
    }
  }, [state.placed, state.center, onChange]);

  // Ready timeout (FR-038).
  useEffect(() => {
    if (state.map !== "loading") return;
    const timer = setTimeout(
      () => dispatch({ type: "timeout" }),
      READY_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [state.map, state.attempt]);

  // Reverse geocode a placed pin when a geocoder exists (FR-031).
  const { placed, center, description } = state;
  useEffect(() => {
    if (!geocoder || !placed || !center || description) return;
    let live = true;
    geocoder
      .reverse(center)
      .then((d) => {
        if (!live) return;
        dispatch({ type: "described", description: d });
        onDescribed?.(d);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [geocoder, placed, center, description, onDescribed]);

  // Place search after 3 characters (FR-030).
  useEffect(() => {
    if (!geocoder || query.trim().length < 3) return;
    let live = true;
    geocoder
      .search(query.trim())
      .then((r) => {
        if (live) setResults(r);
      })
      .catch(() => {
        if (live) setResults([]);
      });
    return () => {
      live = false;
    };
  }, [geocoder, query]);

  function recenterOn(at: LatLng) {
    setRecenter((r) => ({ center: at, seq: (r?.seq ?? 0) + 1 }));
  }

  function pick(p: Place) {
    setResults([]);
    setQuery(p.label);
    dispatch({ type: "picked", center: p.center });
    recenterOn(p.center);
  }

  async function goToCurrentLocation() {
    if (!locate) return;
    setLocateError(null);
    const at = await locate();
    if (at === null) {
      setLocateError(PIN_COPY.locationFailed);
      return;
    }
    dispatch({ type: "located", center: at });
    recenterOn(at);
  }

  const status = state.placed
    ? state.description
      ? PIN_COPY.placedAt(state.description)
      : PIN_COPY.placed
    : "";

  return (
    <View style={styles.root} testID="pin-picker">
      <Text weight="semiBold">{PIN_COPY.title}</Text>
      <Text tone="muted">{PIN_COPY.hint}</Text>
      {geocoder ? (
        <TextField
          label={PIN_COPY.search}
          value={query}
          onChangeText={(v) => {
            setQuery(v);
            if (v.trim().length < 3) setResults([]);
          }}
          kind="name"
        />
      ) : null}
      {results.map((r) => (
        <PressableTarget
          key={r.label}
          onPress={() => pick(r)}
          style={styles.result}
        >
          {r.label}
        </PressableTarget>
      ))}
      {locate ? (
        <Button variant="outline" onPress={() => void goToCurrentLocation()}>
          {PIN_COPY.goToCurrentLocation}
        </Button>
      ) : null}
      {locateError ? <Text style={styles.error}>{locateError}</Text> : null}
      <View style={styles.mapBox}>
        {state.map === "failed" ? (
          <View style={styles.failed}>
            <Text role="alert">{PIN_COPY.failed}</Text>
            <Button
              variant="outline"
              onPress={() => dispatch({ type: "retry" })}
            >
              {PIN_COPY.tryAgain}
            </Button>
          </View>
        ) : (
          <>
            <PinMap
              key={state.attempt}
              initialCenter={initialCenter}
              recenterTo={recenter}
              onCenterChange={(c, byUser) =>
                dispatch({ type: "moved", center: c, byUser })
              }
              onReady={() => dispatch({ type: "ready" })}
              onError={(kind) =>
                dispatch(
                  kind === "tiles"
                    ? { type: "tileError" }
                    : { type: "timeout" },
                )
              }
              accessibilityLabel={PIN_COPY.mapLabel}
              tileUrl={config.tileUrl}
              attribution={config.attribution}
            />
            <View style={styles.overlay}>
              <View style={styles.pin}>
                <View style={styles.pinHead} />
                <View style={styles.pinStem} />
              </View>
            </View>
            <View style={styles.chip}>
              <Text size="xs" weight="semiBold">
                {PIN_COPY.chip}
              </Text>
            </View>
          </>
        )}
      </View>
      <View aria-live="polite" accessibilityLiveRegion="polite">
        {status ? <Text weight="semiBold">{status}</Text> : null}
        {state.placed && state.description ? (
          <Text tone="muted">{PIN_COPY.filled}</Text>
        ) : null}
      </View>
      {error && !state.placed ? (
        <Text style={styles.error}>{error}</Text>
      ) : null}
    </View>
  );
}
